import { isNotEmpty, isNull, isNotNull } from '@satin/core/utility';
import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { SharedConstant } from '@satin/core/constant';
import * as i1 from 'ngx-toastr';

class Message {
    constructor() {
        this.success = {};
        this.info = {};
        this.warning = {};
        this.systemFailure = {};
        this.error = {};
        this.showMessages = false;
    }
    isEmpty() {
        if (isNotEmpty(this.success)) {
            return false;
        }
        if (isNotEmpty(this.info)) {
            return false;
        }
        if (isNotEmpty(this.warning)) {
            return false;
        }
        if (isNotEmpty(this.systemFailure)) {
            return false;
        }
        if (isNotEmpty(this.error)) {
            return false;
        }
        return true;
    }
}

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class MessagesService {
    constructor(toastr) {
        this.toastr = toastr;
        this.successMessageParameter = SharedConstant.SUCCESS_MESSAGE_PARAMETER;
        this.informationMessagesParameter = SharedConstant.INFORMATION_MESSAGE_PARAMETER;
        this.systemFailureMessageParameter = SharedConstant.SYSTEM_FAILURE_MESSAGE_PARAMETER;
        this.errorMessageParameter = SharedConstant.ERROR_MESSAGE_PARAMETER;
        this.messagesSubject = new Subject();
        this.messages = this.messagesSubject.asObservable();
        this._messages = new Message();
    }
    //if we just want to subscribe
    subscribe(fn) {
        return this.messages.subscribe(fn);
    }
    info(message) {
        this.toastr.info(message);
    }
    error(message) {
        return this.toastr.error(message);
    }
    systemFailure(message) {
        return this.toastr.error(message);
    }
    warning(message) {
        return this.toastr.warning(message);
    }
    success(message) {
        return this.toastr.success(message);
    }
    processMessage(model, messageSuppression) {
        if (isNull(messageSuppression) || !messageSuppression.success) {
            this.processSuccessMessages(model);
        }
        if (isNull(messageSuppression) || !messageSuppression.info) {
            this.processInformationMessages(model);
        }
        if (isNull(messageSuppression) || !messageSuppression.error) {
            this.processErrorMessages(model);
        }
        if (isNull(messageSuppression) || !messageSuppression.systemFailure) {
            this.processSystemFailureMessages(model);
        }
        return this.isSuccess(model);
    }
    isSuccess(model) {
        // If there is a success message, then it's a success
        if (isNotNull(model[this.successMessageParameter])) {
            return true;
            // If there is an error message or system failure, then it's an error
        }
        else if (isNotNull(model[this.errorMessageParameter])
            || isNotNull(model[this.systemFailureMessageParameter])) {
            return false;
            // Otherwise assume a success
        }
        else {
            return true;
        }
    }
    processSuccessMessages(model) {
        if (isNotNull(model[this.successMessageParameter])) {
            this.success(model[this.successMessageParameter]);
        }
    }
    ;
    processInformationMessages(model) {
        if (isNotNull(model[this.informationMessagesParameter])) {
            for (let i = 0; i < model[this.informationMessagesParameter].length; i++) {
                this.info(model[this.informationMessagesParameter][i]);
            }
        }
    }
    ;
    processErrorMessages(model, dontShowErrorMessage) {
        if (isNotNull(model[this.errorMessageParameter])) {
            for (let i = 0; i < model[this.errorMessageParameter].length; i++) {
                this.error(model[this.errorMessageParameter][i].message);
            }
        }
    }
    processSystemFailureMessages(model) {
        if (isNotNull(model[this.systemFailureMessageParameter])) {
            this.systemFailure(model[this.systemFailureMessageParameter]);
        }
    }
    processHttpFailureMessage(err) {
        this.toastr.error(this.getErrorMessageToDisplay(err));
    }
    getErrorMessageToDisplay(error) {
        let errorMessage = '';
        console.error(error);
        if (isNull(error)
            || isNull(error.status)
            || error.status === 0
            || error.status === 404
            || error.status === 503
            || error.status === 504) {
            errorMessage = 'Request could not reach server, please re-login. If this issue persists, please contact a system administrator.';
        }
        else if (error.status === 401
            || error.status === 403) {
            errorMessage = 'Access denied.';
        }
        else if (error.status === 500) {
            errorMessage = 'Operation failed. Please contact a system administrator.';
        }
        else {
            const errorBody = error.error;
            if (Array.isArray(errorBody.errorList) && errorBody.errorList.length > 0) {
                errorMessage = errorBody.errorList.map(e => e.message).join(', ');
            }
            else if (isNotNull(errorBody.globalErrors) && errorBody.globalErrors.length > 0) {
                errorMessage = errorBody.globalErrors[0].code;
            }
            else {
                errorMessage = error.statusText;
            }
        }
        return errorMessage;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: MessagesService, deps: [{ token: i1.ToastrService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: MessagesService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: MessagesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.ToastrService }]; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class MessageSuppression {
    constructor(success, error, systemFailure, info) {
        this.success = false;
        this.info = false;
        this.warning = false;
        this.systemFailure = false;
        this.error = false;
        this.success = success ? success : false;
        this.error = error ? error : false;
        this.systemFailure = systemFailure ? systemFailure : false;
        this.info = info ? info : false;
    }
}

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Message, MessageSuppression, MessagesService };
